
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getMenuItemsWithInventory } from '@/services/inventory/inventoryService';
import { ExternalLink, Package, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface MenuInventoryRelationshipsProps {
  storeId: string;
  isAdvancedMode: boolean;
}

export function MenuInventoryRelationships({ storeId, isAdvancedMode }: MenuInventoryRelationshipsProps) {
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (storeId && isAdvancedMode) {
      fetchMenuItemsWithInventory();
    }
  }, [storeId, isAdvancedMode]);

  const fetchMenuItemsWithInventory = async () => {
    setLoading(true);
    try {
      // Specifically fetch menu items with advanced inventory relationships
      const items = await getMenuItemsWithInventory(storeId, 'advanced');
      
      // Filter to only include items that actually have inventory relationships
      const itemsWithRelationships = items.filter(item => 
        item.inventoryRelationships && item.inventoryRelationships.length > 0
      );
      
      setMenuItems(itemsWithRelationships);
      setError(null);
    } catch (err) {
      console.error('Error fetching menu items with inventory:', err);
      setError('Failed to load menu-inventory relationships');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchMenuItemsWithInventory();
  };

  if (!isAdvancedMode) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <div className="bg-muted/50 rounded-lg p-6 text-center">
          <h4 className="font-medium mb-2">Advanced Inventory Mode is disabled</h4>
          <p className="text-muted-foreground mb-4">
            Enable Advanced Inventory Mode to link menu items with inventory ingredients for automatic tracking.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium flex items-center">
            <Package className="mr-2 h-5 w-5" />
            Menu-Inventory Relationships
          </h3>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-full" />
              </div>
            ))}
          </div>
        ) : menuItems.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No menu items with inventory relationships found.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Link ingredients to menu items in the Menu Management section.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Menu Item</TableHead>
                  <TableHead>Inventory Status</TableHead>
                  <TableHead>Ingredients</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {menuItems.map((menuItem) => (
                  <TableRow key={menuItem.id}>
                    <TableCell>
                      <div className="font-medium">{menuItem.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {menuItem.available ? 'Available' : 'Unavailable'} • ${Number(menuItem.price).toFixed(2)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {menuItem.inventory_controlled ? (
                        <Badge variant="outline" className="border-green-500 text-green-500">
                          Inventory Controlled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-muted-foreground text-muted-foreground">
                          Not Tracked
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {menuItem.inventoryRelationships && menuItem.inventoryRelationships.length > 0 ? (
                        <div className="space-y-1">
                          {menuItem.inventoryRelationships.map((rel: any) => (
                            <div key={rel.inventory_item_id} className="text-sm flex items-center">
                              <span>{rel.quantity_required} x </span>
                              <span className="font-medium ml-1">{rel.inventory_items?.name || 'Unknown'}</span>
                              <span className="ml-1 text-muted-foreground">
                                ({rel.inventory_items?.unit_of_measure || 'unit'})
                              </span>
                              {rel.inventory_items?.current_quantity < rel.quantity_required && (
                                <Badge variant="destructive" className="ml-2 text-xs">
                                  Low Stock
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">No ingredients linked</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Card>
  );
}
